import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { isAgent, isAdmin, isStaff, isSuperAdmin, isStudent } from '../../utils/roleUtils'
import { ShieldCheck, CheckCircle2 } from 'lucide-react'
import PageHeader from '../../components/common/PageHeader'
import axiosInstance from '../../services/axiosInstance'
import './styles.scss'

export default function RoleValidation() {
  const { user, login } = useAuth()
  const navigate = useNavigate()
  const [confirmed, setConfirmed] = useState(false)

  const [isEditing, setIsEditing] = useState(false)
  const [selectedRole, setSelectedRole] = useState(() => {
    if (isSuperAdmin(user)) return 'SUPER_ADMIN'
    if (isAdmin(user)) return 'ADMIN'
    if (isStaff(user)) return 'STAFF'
    if (isAgent(user)) return 'AGENT'
    if (isStudent(user)) return 'USER'
    return 'USER'
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const getRoleName = (r) => {
    switch (r) {
      case 'SUPER_ADMIN': return 'Super Administrator'
      case 'ADMIN': return 'Administrator'
      case 'STAFF': return 'Admissions Staff'
      case 'AGENT': return 'Recruitment Agent'
      case 'USER': return 'Student'
      default: return 'User'
    }
  }

  const handleConfirm = async () => {
    setLoading(true)
    setError('')

    try {
      // 1. Update Role via API
      const res = await axiosInstance.put('/api/users/me/role', { role: selectedRole })
      
      if (res.data.success) {
        // 2. Fetch fresh user profile
        const profileRes = await axiosInstance.get('/api/users/me')
        if (profileRes.data.success) {
          login(profileRes.data.token, profileRes.data.user)
          
          setConfirmed(true)
          setTimeout(() => {
            if (selectedRole === 'USER') {
              navigate('/complete-profile')
            } else {
              navigate('/pending-approval')
            }
          }, 1500)
        } else {
          setError('Failed to refresh user profile.')
        }
      } else {
        setError(res.data.message || 'Failed to update role.')
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="role-validation-page">
      <PageHeader 
        title="Role Validation" 
        subtitle="Please verify your account type before proceeding" 
      />

      <div className="validation-content glass-panel">
        <div className="status-icon">
          {confirmed ? <CheckCircle2 size={48} color="#10B981" /> : <ShieldCheck size={48} color="#2563EB" />}
        </div>
        
        <h2>Verify Your Role</h2>
        
        <p className="status-message">
          You have successfully created your account. Before you continue, please validate that you have registered for the correct role.
        </p>
        
        {error && <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}

        <div className="role-box">
          <span className="role-label">Registered Role:</span>
          
          {isEditing ? (
            <select 
              value={selectedRole} 
              onChange={(e) => setSelectedRole(e.target.value)}
              className="role-select"
              style={{ padding: '0.5rem', borderRadius: '8px', border: '1px solid #ccc', marginTop: '0.5rem', fontSize: '1.1rem' }}
            >
              <option value="USER">Student</option>
              <option value="AGENT">Recruitment Agent</option>
              <option value="STAFF">Admissions Staff</option>
              <option value="ADMIN">Administrator</option>
            </select>
          ) : (
            <span className="role-value">{getRoleName(selectedRole)}</span>
          )}

          {!confirmed && (
            <button 
              className="btn btn-ghost" 
              onClick={() => setIsEditing(!isEditing)}
              style={{ marginTop: '0.5rem', alignSelf: 'flex-start', padding: 0, color: 'var(--primary-blue)' }}
            >
              {isEditing ? 'Cancel Edit' : 'Change Role'}
            </button>
          )}
        </div>

        {confirmed ? (
          <div className="success-message">
            <p>Role successfully validated! Redirecting you now...</p>
          </div>
        ) : (
          <div className="action-buttons">
            <button 
              className="btn btn-primary btn-full-width" 
              onClick={handleConfirm} 
              disabled={loading}
              style={{ height: '48px', fontSize: '1.05rem' }}
            >
              {loading ? 'Saving...' : 'Confirm Role & Continue'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
